import { Badge, Center, Group, Loader, Paper, Select, Stack, Text, TextInput, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { DocumentReference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { ALL_NOTIFICATIONS, NotificationAudience, NotificationCategory } from '@polarissleep/shared';
import { DataTable } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { TEMPLATE_TYPE_SYSTEM } from '../../../lib/template-renderers/constants';
import { PageHeader } from '../../components/layout/PageHeader';
import { TableAnchor } from '../../components/layout/TableAnchor';
import {
  getDocRefPath,
  isLegacyPath,
  parseTemplatePath,
  type TemplateChannel,
  templateChannelsForNotification,
} from './notification-template-helpers';

const CHANNEL_COLORS: Record<TemplateChannel, string> = {
  'email-subject': 'cyan',
  'email-body': 'blue',
  'in-app-body': 'orange',
  'sms-body': 'green',
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  ...Object.values(NotificationCategory).map((c) => ({
    value: c,
    label: c.charAt(0) + c.slice(1).toLowerCase(),
  })),
];

const AUDIENCE_OPTIONS = [
  { value: '', label: 'All Audiences' },
  { value: NotificationAudience.PATIENT, label: 'Patient' },
  { value: NotificationAudience.PROVIDER, label: 'Provider' },
];

type NotificationDef = (typeof ALL_NOTIFICATIONS)[keyof typeof ALL_NOTIFICATIONS];

export function NotificationTemplateList() {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<DocumentReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const results = await medplum.searchResources('DocumentReference', {
        _tag: `${TEMPLATE_TYPE_SYSTEM}|notification-template`,
        _count: '200',
        _sort: '-_lastUpdated',
      });
      setTemplates(results);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  }, [medplum]);

  useEffect(() => {
    fetchTemplates().catch(console.error);
  }, [fetchTemplates]);

  const docRefsByKey = useMemo(() => {
    const map: Record<string, Partial<Record<TemplateChannel, DocumentReference>>> = {};
    for (const docRef of templates) {
      const path = getDocRefPath(docRef);
      if (!path) {
        continue;
      }
      const parsed = parseTemplatePath(path);
      if (!parsed) {
        continue;
      }
      if (!(parsed.notificationKey in ALL_NOTIFICATIONS)) {
        continue;
      }
      if (!map[parsed.notificationKey]) {
        map[parsed.notificationKey] = {};
      }
      map[parsed.notificationKey][parsed.channel] = docRef;
    }
    return map;
  }, [templates]);

  const legacyTemplates = useMemo(() => templates.filter((t) => isLegacyPath(getDocRefPath(t) ?? '')), [templates]);

  const searchLower = debouncedSearch.toLowerCase();

  const filteredNotifications = useMemo<NotificationDef[]>(
    () =>
      Object.values(ALL_NOTIFICATIONS).filter((def) => {
        const matchesSearch =
          !searchLower ||
          def.key.toLowerCase().includes(searchLower) ||
          def.label.toLowerCase().includes(searchLower) ||
          def.description.toLowerCase().includes(searchLower);
        const matchesCategory = !categoryFilter || def.category === categoryFilter;
        const matchesAudience = !audienceFilter || def.audience === audienceFilter;
        return matchesSearch && matchesCategory && matchesAudience;
      }),
    [searchLower, categoryFilter, audienceFilter]
  );

  const mostRecentUpdateFor = (key: string): string | null => {
    const docRefs = Object.values(docRefsByKey[key] ?? {});
    const dates = docRefs.map((d) => d?.meta?.lastUpdated).filter((d): d is string => typeof d === 'string');
    if (dates.length === 0) {
      return null;
    }
    return dates.sort().at(-1) ?? null;
  };

  if (loading) {
    return (
      <Stack gap="md" p="md">
        <PageHeader title="Notification Templates" />
        <Paper withBorder radius="md" shadow="sm" p="md">
          <Center py="xl">
            <Loader />
          </Center>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md" p="md">
      <PageHeader title="Notification Templates" />

      <Paper withBorder radius="md" shadow="sm" p="md">
        <Stack gap="md">
          <Text c="dimmed" size="sm">
            Edit notification templates to customize the content sent to patients and providers. Changes take effect
            immediately.
          </Text>

          <Group>
            <TextInput
              placeholder="Search notifications..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Select
              data={AUDIENCE_OPTIONS}
              value={audienceFilter}
              onChange={(v) => setAudienceFilter(v ?? '')}
              placeholder="Audience"
              clearable
            />
            <Select
              data={CATEGORY_OPTIONS}
              value={categoryFilter}
              onChange={(v) => setCategoryFilter(v ?? '')}
              placeholder="Category"
              clearable
            />
          </Group>

          <DataTable
            minHeight={200}
            records={filteredNotifications}
            idAccessor="key"
            striped
            highlightOnHover
            fz="sm"
            noRecordsText="No notifications match the current filters."
            onRowClick={({ record }) =>
              navigate(`/admin/notification-templates/by-key/${record.key}`)?.catch(console.error)
            }
            columns={[
              {
                accessor: 'label',
                title: 'Notification',
                render: (def: NotificationDef) => (
                  <Stack gap={2}>
                    <TableAnchor
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/notification-templates/by-key/${def.key}`)?.catch(console.error);
                      }}
                    >
                      {def.label}
                    </TableAnchor>
                    <Text size="xs" c="dimmed" ff="monospace">
                      {def.key}
                    </Text>
                  </Stack>
                ),
              },
              {
                accessor: 'audience',
                title: 'Audience',
                render: (def: NotificationDef) => (
                  <Badge size="xs" variant="outline" color="gray">
                    {def.audience.toLowerCase()}
                  </Badge>
                ),
              },
              {
                accessor: 'category',
                title: 'Category',
                render: (def: NotificationDef) => (
                  <Badge size="xs" variant="outline" color="gray">
                    {def.category.toLowerCase()}
                  </Badge>
                ),
              },
              {
                accessor: 'channels',
                title: 'Channels',
                render: (def: NotificationDef) => {
                  const channels = templateChannelsForNotification(def);
                  const existingByChannel = docRefsByKey[def.key] ?? {};
                  return (
                    <Group gap="xs">
                      {channels.map((channel) => {
                        const configured = channel in existingByChannel;
                        return (
                          <Badge
                            key={channel}
                            size="xs"
                            variant={configured ? 'light' : 'outline'}
                            color={configured ? CHANNEL_COLORS[channel] : 'gray'}
                          >
                            {channel}
                          </Badge>
                        );
                      })}
                    </Group>
                  );
                },
              },
              {
                accessor: 'lastUpdated',
                title: 'Last Updated',
                render: (def: NotificationDef) => {
                  const lastUpdated = mostRecentUpdateFor(def.key);
                  return (
                    <Text size="sm" c="dimmed">
                      {lastUpdated ? new Date(lastUpdated).toLocaleDateString() : '—'}
                    </Text>
                  );
                },
              },
            ]}
          />
        </Stack>
      </Paper>

      {legacyTemplates.length > 0 && (
        <Paper withBorder radius="md" shadow="sm" p="md">
          <Stack gap="md">
            <Title order={3} fw={700} size="1.5rem">
              Other templates
            </Title>
            <Text size="sm" c="dimmed">
              Shared or legacy templates not tied to a specific notification key. Edited one file at a time.
            </Text>
            <DataTable
              minHeight={150}
              records={legacyTemplates}
              idAccessor={(d: DocumentReference) => d.id ?? ''}
              striped
              highlightOnHover
              fz="sm"
              noRecordsText="No legacy templates."
              onRowClick={({ record }) => navigate(`/admin/notification-templates/${record.id}`)?.catch(console.error)}
              columns={[
                {
                  accessor: 'template',
                  title: 'Template',
                  render: (docRef: DocumentReference) => {
                    const path = getDocRefPath(docRef) ?? 'unknown';
                    return (
                      <TableAnchor
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/notification-templates/${docRef.id}`)?.catch(console.error);
                        }}
                      >
                        {path}
                      </TableAnchor>
                    );
                  },
                },
                {
                  accessor: 'channel',
                  title: 'Channel',
                  render: (docRef: DocumentReference) => {
                    const channel = docRef.category?.[0]?.coding?.[0]?.code ?? 'unknown';
                    return (
                      <Badge size="sm" variant="light" color={CHANNEL_COLORS[channel as TemplateChannel] ?? 'gray'}>
                        {channel}
                      </Badge>
                    );
                  },
                },
                {
                  accessor: 'lastUpdated',
                  title: 'Last Updated',
                  render: (docRef: DocumentReference) => (
                    <Text size="sm" c="dimmed">
                      {docRef.meta?.lastUpdated ? new Date(docRef.meta.lastUpdated).toLocaleDateString() : '—'}
                    </Text>
                  ),
                },
              ]}
            />
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
